import { useState } from "react";
import { KeyRound, Shield, Loader2, ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  phone?: string;
  onClose: () => void;
  onCompleted?: () => void;
}

export function AdminSetupModal({ open, phone, onClose, onCompleted }: Props) {
  const [step, setStep] = useState<"initiate" | "verify" | "done">("initiate");
  const [sessionToken, setSessionToken] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  async function handleSendCode() {
    if (!phone) {
      setError("No phone number found for this account.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.adminSetupInitiate({ phone });
      setSessionToken(res.session_token);
      setStep("verify");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to send verification code. Please ensure your Telegram is linked.");
    } finally {
      setLoading(false);
    }
  }

  async function handleEnrollPassword(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim().length !== 6) {
      setError("Please enter the 6-digit verification code sent to your Telegram.");
      return;
    }
    if (password.length < 8) {
      setError("Administrator password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await api.adminSetupPassword({
        session_token: sessionToken,
        code: code.trim(),
        password,
      });
      setStep("done");
      if (onCompleted) onCompleted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Enrollment failed. Please verify your code and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="md"
      title="Admin Credential Enrollment"
      description="Setup required for promoted administrator account"
      className="p-6"
    >
      <div className="space-y-4">

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-xs text-destructive">
            {error}
          </div>
        )}

        {step === "initiate" && (
          <div className="space-y-4 text-xs">
            <p className="text-muted-foreground leading-relaxed">
              Your account has been granted administrator privileges. To complete the promotion, please verify your identity via your linked Telegram account and choose an administrator password.
            </p>
            {phone && (
              <div className="p-3 bg-muted/40 rounded-md border border-border/60">
                <span className="text-muted-foreground">Registered Phone: </span>
                <span className="font-semibold text-foreground font-mono">{phone}</span>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 border border-border rounded-md text-foreground hover:bg-muted text-xs font-semibold"
              >
                Later
              </button>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={loading}
                className="px-4 py-2 bg-primary text-on-accent rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                Send Telegram Code
              </button>
            </div>
          </div>
        )}

        {step === "verify" && (
          <form onSubmit={handleEnrollPassword} className="space-y-3 text-xs">
            <p className="text-muted-foreground leading-relaxed">
              We sent a 6-digit verification code to your linked Telegram account. Enter the code and set your new administrator password.
            </p>

            <div>
              <label className="block font-semibold text-foreground mb-1">6-Digit Verification Code</label>
              <input
                type="text"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="w-full px-3 py-2 text-center text-lg tracking-widest font-mono rounded-md border border-border bg-background focus:ring-1 focus:ring-primary"
                autoFocus
              />
            </div>

            <div>
              <label className="block font-semibold text-foreground mb-1">New Administrator Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="w-full px-3 py-2 rounded-md border border-border bg-background focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block font-semibold text-foreground mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                className="w-full px-3 py-2 rounded-md border border-border bg-background focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setStep("initiate")}
                className="px-3 py-2 border border-border rounded-md text-foreground hover:bg-muted font-semibold text-xs"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary text-on-accent rounded-md font-semibold text-xs flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                Verify & Set Password
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-4 text-center py-2 text-xs">
            <div className="w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto">
              <CheckCircle2 size={24} />
            </div>
            <div>
              <h4 className="font-bold text-sm text-foreground">Password Enrolled Successfully</h4>
              <p className="text-muted-foreground mt-1 leading-relaxed">
                Your administrator credentials are ready. For security, please sign in with your new password and 2FA code to access the administrator panel.
              </p>
            </div>
            <a
              href="/auth/login"
              className="inline-flex items-center justify-center gap-1.5 w-full py-2.5 bg-primary text-on-accent font-semibold rounded-md hover:bg-primary/90 transition"
            >
              Go to Administrator Login <ArrowRight size={14} />
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
}
